/**
 * Shift Detail Page - Microsoft 365 Style
 * View shift details and manage patrol windows
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Badge,
  Spinner,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  ClockRegular,
  AddRegular,
  EditRegular,
  DeleteRegular,
  CalendarRegular,
  TimerRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SlidePanel } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import type { PatrolWindow } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '8px',
  },
  headerContent: {
    flex: 1,
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
    marginTop: '4px',
  },
  shiftInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  infoCard: {
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  infoLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#605e5c',
    display: 'block',
    marginBottom: '4px',
  },
  infoValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
  },
  windowsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  windowCard: {
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  windowInfo: {
    flex: 1,
  },
  windowName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    display: 'block',
    marginBottom: '4px',
  },
  windowDetails: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '8px',
  },
  windowTimes: {
    fontSize: '13px',
    color: '#605e5c',
    backgroundColor: '#f3f2f1',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
  },
  windowActions: {
    display: 'flex',
    gap: '8px',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#605e5c',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
    border: '1px dashed #e1dfdd',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  previewSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#f3f2f1',
    borderRadius: '8px',
  },
  previewTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
    display: 'block',
  },
  previewTimes: {
    fontSize: '13px',
    color: '#605e5c',
    lineHeight: '1.5',
  },
  summaryCard: {
    padding: '16px',
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: '8px',
    marginTop: '16px',
  },
  summaryText: {
    fontSize: '14px',
    color: tokens.colorBrandForeground2,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px',
  },
});

interface ShiftDetail {
  id: string;
  name: string;
  shift_type?: string;
  start_time?: string;
  end_time?: string;
  clock_count?: number;
  time_tolerance?: number;
  patrol_time_interval?: number;
  patrol_mode?: 'anytime' | 'scheduled';
  default_tolerance_minutes?: number;
}

interface WindowFormData {
  windowName: string;
  startTime: string;
  endTime: string;
  intervalMinutes: string;
  toleranceMinutes: string;
}

const INTERVAL_OPTIONS = [
  { value: '15', label: 'Every 15 minutes' },
  { value: '30', label: 'Every 30 minutes' },
  { value: '45', label: 'Every 45 minutes' },
  { value: '60', label: 'Every 1 hour' },
  { value: '120', label: 'Every 2 hours' },
];

const TOLERANCE_OPTIONS = [
  { value: '5', label: '± 5 minutes' },
  { value: '10', label: '± 10 minutes' },
  { value: '15', label: '± 15 minutes' },
];

// Helper function to calculate expected patrol times
const calculateExpectedTimes = (
  startTime: string,
  endTime: string,
  intervalMinutes: number
): string[] => {
  if (!startTime || !endTime || intervalMinutes <= 0) {
    return [];
  }

  const times: string[] = [];
  const startParts = startTime.split(':').map(Number);
  const endParts = endTime.split(':').map(Number);

  const startHours = startParts[0] ?? 0;
  const startMins = startParts[1] ?? 0;
  const endHours = endParts[0] ?? 0;
  const endMins = endParts[1] ?? 0;

  let currentMinutes = startHours * 60 + startMins;
  const endMinutes = endHours * 60 + endMins;

  while (currentMinutes <= endMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const mins = currentMinutes % 60;
    times.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
    currentMinutes += intervalMinutes;
  }

  return times;
};

export const ShiftDetailFluentUI: React.FC = () => {
  const styles = useStyles();
  const { shiftId } = useParams<{ shiftId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState<ShiftDetail | null>(null);
  const [windows, setWindows] = useState<PatrolWindow[]>([]);
  const [windowPanelOpen, setWindowPanelOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWindow, setSelectedWindow] = useState<PatrolWindow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<WindowFormData>({
    windowName: '',
    startTime: '',
    endTime: '',
    intervalMinutes: '30',
    toleranceMinutes: '5',
  });

  const fetchShiftDetails = useCallback(async () => {
    if (!shiftId) {
      return;
    }

    setLoading(true);
    try {
      // Fetch shift details
      const shiftResponse = await ApiClient.get('/get-shifts');
      if (shiftResponse.data.status === 'success') {
        const shiftData = shiftResponse.data.data?.find(
          (s: ShiftDetail) => String(s.id) === shiftId
        );
        if (shiftData) {
          setShift(shiftData);
        }
      }

      // Fetch patrol windows
      const windowsResponse = await ApiClient.get(`/get-patrol-windows/${shiftId}`);
      if (windowsResponse.data.status === 'success') {
        setWindows(windowsResponse.data.data?.windows || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [shiftId]);

  useEffect(() => {
    fetchShiftDetails();
  }, [fetchShiftDetails]);

  const handleOpenWindowPanel = (window?: PatrolWindow) => {
    if (window) {
      setSelectedWindow(window);
      setFormData({
        windowName: window.window_name || '',
        startTime: window.start_time?.substring(0, 5) || '',
        endTime: window.end_time?.substring(0, 5) || '',
        intervalMinutes: String(window.interval_minutes),
        toleranceMinutes: String(window.tolerance_minutes),
      });
    } else {
      setSelectedWindow(null);
      setFormData({
        windowName: '',
        startTime: '',
        endTime: '',
        intervalMinutes: '30',
        toleranceMinutes: '5',
      });
    }
    setWindowPanelOpen(true);
  };

  const handleSaveWindow = async () => {
    if (!formData.startTime || !formData.endTime) {
      newToast('warning', 'Please enter start and end times');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        shift_id: Number(shiftId),
        window_name: formData.windowName || null,
        start_time: formData.startTime + ':00',
        end_time: formData.endTime + ':00',
        interval_minutes: parseInt(formData.intervalMinutes),
        tolerance_minutes: parseInt(formData.toleranceMinutes),
      };

      let response;
      if (selectedWindow) {
        response = await ApiClient.put(`/update-patrol-window/${selectedWindow.id}`, payload);
      } else {
        response = await ApiClient.post('/create-patrol-window', payload);
      }

      if (response.data.status === 'success') {
        newToast('success', `Patrol window ${selectedWindow ? 'updated' : 'created'} successfully`);
        setWindowPanelOpen(false);
        fetchShiftDetails();
      } else {
        newToast('error', response.data?.message || 'Failed to save patrol window');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSubmitting(false);
  };

  const handleDeleteWindow = async () => {
    if (!selectedWindow) {
      return;
    }

    try {
      const response = await ApiClient.delete(`/delete-patrol-window/${selectedWindow.id}`);
      if (response.data.status === 'success') {
        newToast('success', 'Patrol window deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedWindow(null);
        fetchShiftDetails();
      } else {
        newToast('error', 'Failed to delete patrol window');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  // Calculate preview times
  const previewTimes = calculateExpectedTimes(
    formData.startTime,
    formData.endTime,
    parseInt(formData.intervalMinutes) || 0
  );

  // Calculate total expected patrols
  const totalExpectedPatrols = windows.reduce((sum, w) => sum + (w.expected_patrol_count || 0), 0);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading shift details..." />
      </div>
    );
  }

  if (!shift) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/guard-operations/shifts')}
          >
            Back to Shifts
          </Button>
        </div>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Shift not found
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/guard-operations/shifts')}
        >
          Back
        </Button>
        <div className={styles.headerContent}>
          <Text className={styles.pageTitle}>{shift.name}</Text>
          <Text className={styles.pageSubtitle}>Shift Details & Patrol Windows Configuration</Text>
        </div>
        <Badge
          appearance="filled"
          color={shift.patrol_mode === 'scheduled' ? 'success' : 'informative'}
        >
          {shift.patrol_mode === 'scheduled' ? 'Scheduled Patrols' : 'Anytime Patrols'}
        </Badge>
      </div>

      {/* Shift Info Cards */}
      <div className={styles.shiftInfo}>
        <div className={styles.infoCard}>
          <Text className={styles.infoLabel}>Start Time</Text>
          <Text className={styles.infoValue}>
            <ClockRegular />
            {shift.start_time || '—'}
          </Text>
        </div>
        <div className={styles.infoCard}>
          <Text className={styles.infoLabel}>End Time</Text>
          <Text className={styles.infoValue}>
            <ClockRegular />
            {shift.end_time || '—'}
          </Text>
        </div>
        <div className={styles.infoCard}>
          <Text className={styles.infoLabel}>Shift Type</Text>
          <Text className={styles.infoValue}>{shift.shift_type || 'General'}</Text>
        </div>
        <div className={styles.infoCard}>
          <Text className={styles.infoLabel}>Time Tolerance</Text>
          <Text className={styles.infoValue}>
            {shift.time_tolerance ? `${shift.time_tolerance} min` : '—'}
          </Text>
        </div>
      </div>

      {/* Patrol Windows Section */}
      <ContentCard>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>Patrol Windows</Text>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={() => handleOpenWindowPanel()}
          >
            Add Window
          </Button>
        </div>

        {windows.length === 0 ? (
          <div className={styles.emptyState}>
            <CalendarRegular style={{ fontSize: '32px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No patrol windows configured
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Guards can patrol anytime during shift hours. Add windows to enforce scheduled
              patrols.
            </Text>
          </div>
        ) : (
          <div className={styles.windowsGrid}>
            {windows.map(window => (
              <div key={window.id} className={styles.windowCard}>
                <div className={styles.windowInfo}>
                  <Text className={styles.windowName}>
                    {window.window_name || `Window ${window.id}`}
                  </Text>
                  <div className={styles.windowDetails}>
                    <span>
                      <ClockRegular style={{ marginRight: '4px' }} />
                      {window.start_time?.substring(0, 5)} - {window.end_time?.substring(0, 5)}
                    </span>
                    <span>
                      <TimerRegular style={{ marginRight: '4px' }} />
                      Every {window.interval_minutes} min
                    </span>
                    <span>± {window.tolerance_minutes} min tolerance</span>
                  </div>
                  {window.expected_patrol_times && window.expected_patrol_times.length > 0 && (
                    <div className={styles.windowTimes}>
                      <strong>Expected: </strong>
                      {window.expected_patrol_count} patrols (
                      {window.expected_patrol_times
                        .slice(0, 5)
                        .map(t => t.substring(0, 5))
                        .join(', ')}
                      {window.expected_patrol_times.length > 5 && '...'})
                    </div>
                  )}
                </div>
                <div className={styles.windowActions}>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<EditRegular />}
                    onClick={() => handleOpenWindowPanel(window)}
                  />
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    onClick={() => {
                      setSelectedWindow(window);
                      setDeleteDialogOpen(true);
                    }}
                  />
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className={styles.summaryCard}>
              <Text className={styles.summaryText}>
                <CheckmarkCircleRegular />
                Total Expected Patrols per Shift: <strong>{totalExpectedPatrols}</strong>
              </Text>
            </div>
          </div>
        )}
      </ContentCard>

      {/* Add/Edit Window SlidePanel */}
      <SlidePanel
        open={windowPanelOpen}
        onOpenChange={setWindowPanelOpen}
        title={selectedWindow ? 'Edit Patrol Window' : 'Add Patrol Window'}
        subtitle={selectedWindow ? 'Update window configuration' : 'Configure a new patrol window'}
        width="400px"
        footer={
          <>
            <Button
              appearance="secondary"
              onClick={() => setWindowPanelOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSaveWindow} disabled={submitting}>
              {submitting ? 'Saving...' : selectedWindow ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Field label="Window Name" hint="Optional - e.g., Morning Patrol, Afternoon Round">
            <Input
              value={formData.windowName}
              onChange={e => setFormData({ ...formData, windowName: e.target.value })}
              placeholder="Enter window name"
            />
          </Field>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Field label="Start Time" required>
              <Input
                type="time"
                value={formData.startTime}
                onChange={e => setFormData({ ...formData, startTime: e.target.value })}
              />
            </Field>
          </div>
          <div className={styles.formField}>
            <Field label="End Time" required>
              <Input
                type="time"
                value={formData.endTime}
                onChange={e => setFormData({ ...formData, endTime: e.target.value })}
              />
            </Field>
          </div>
        </div>

        <div className={styles.formField}>
          <Field label="Patrol Interval" required>
            <Dropdown
              value={
                INTERVAL_OPTIONS.find(o => o.value === formData.intervalMinutes)?.label ||
                'Select interval'
              }
              selectedOptions={[formData.intervalMinutes]}
              onOptionSelect={(_, data) =>
                setFormData({ ...formData, intervalMinutes: data.optionValue as string })
              }
            >
              {INTERVAL_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        <div className={styles.formField}>
          <Field label="Tolerance (Grace Period)">
            <Dropdown
              value={
                TOLERANCE_OPTIONS.find(o => o.value === formData.toleranceMinutes)?.label ||
                'Select tolerance'
              }
              selectedOptions={[formData.toleranceMinutes]}
              onOptionSelect={(_, data) =>
                setFormData({ ...formData, toleranceMinutes: data.optionValue as string })
              }
            >
              {TOLERANCE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {/* Preview Section */}
        {previewTimes.length > 0 && (
          <div className={styles.previewSection}>
            <Text className={styles.previewTitle}>
              Preview: {previewTimes.length} patrols expected
            </Text>
            <Text className={styles.previewTimes}>{previewTimes.join(', ')}</Text>
          </div>
        )}
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Patrol Window</DialogTitle>
            <DialogContent>
              Are you sure you want to delete{' '}
              <strong>{selectedWindow?.window_name || 'this patrol window'}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteWindow}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ShiftDetailFluentUI;
